/**
 * Overlay indicators — spec §F5.5 ("Overlay symbology (single mode only)").
 *
 * "`overlays[0]` = graduated circles: centroid markers …, radius `(lvl===0?10:5) + √t ·
 * (lvl===0?26:15)` where t = min-max normalised value; fill overlay colour, opacity .82,
 * white 1.6 stroke. `overlays[1]` = rings: radius+2, no fill, weight 3. Colour resolution:
 * user override else auto-contrast pool logic (G4) — **recomputed whenever fill ramp
 * changes**. Tooltips value+unit. Max 2 overlays (toast). Vector pane above choropleth."
 *
 * Everything numeric and every colour comes from `useOverlayClasses()`, which the §F5.6(c)
 * legend blocks also read — so the legend's 3-size key and its recolour swatch describe the
 * markers on the map by construction, not by convention.
 *
 * **No base indicator is required.** The mockup calls `renderOverlays()` unconditionally,
 * including from the branch that clears `state.indicator`, and §F5.5 never mentions the
 * fill layer except as the thing the colours contrast *against*. With no indicator mapped
 * §G4 contrasts against the green fallback ramp (`useFillRampLastStop`).
 */
import { useEffect } from 'react';
import L from 'leaflet';

import { fmtNum } from '../../lib/format';
import { escapeHtml } from '../../lib/html';
import { useMap } from './mapContext';
import { useOverlayClasses, type OverlayClasses } from './overlayClasses';

/** §F5.5 circle: "fill overlay colour, opacity .82, white 1.6 stroke". */
const FILL_OPACITY = 0.82;
const CIRCLE_STROKE_WEIGHT = 1.6;
/** §F5.5 ring: "radius+2, no fill, weight 3". `.95` opacity is the mockup's. */
const RING_RADIUS_BONUS = 2;
const RING_WEIGHT = 3;
const RING_OPACITY = 0.95;

function OverlayLayer({ cls }: { cls: OverlayClasses }) {
  const map = useMap();
  const { index, color, points, name, unitLabel, fmt } = cls;

  useEffect(() => {
    if (!points.length) return;

    const markers = points.map((p) => {
      const marker = L.circleMarker(p.at, {
        pane: 'vectorPane',
        ...(index === 0
          ? {
              radius: p.radius,
              fillColor: color,
              fillOpacity: FILL_OPACITY,
              color: '#fff',
              weight: CIRCLE_STROKE_WEIGHT,
            }
          : {
              radius: p.radius + RING_RADIUS_BONUS,
              fill: false,
              color,
              weight: RING_WEIGHT,
              opacity: RING_OPACITY,
            }),
      });
      // The mockup's overlay tooltip, verbatim in shape: the unit, then the *indicator's
      // own* name and value — an overlay marker sits on top of a choropleth tooltip for the
      // same polygon, so it has to name which indicator it is reporting.
      // `unitName` is `/hierarchy/units` text and `name`/`unitLabel` are `/indicators`
      // text — server strings reaching `innerHTML` (see `lib/html.ts`).
      marker.bindTooltip(
        `<b>${escapeHtml(p.unitName)}</b><br>${escapeHtml(name)}: ` +
          `<b>${fmtNum(p.value, fmt)}</b> ${escapeHtml(unitLabel)}`,
        { sticky: true, className: 'unit-tip' },
      );
      return marker;
    });

    const group = L.layerGroup(markers).addTo(map);
    return () => {
      map.removeLayer(group);
    };
  }, [map, index, color, points, name, unitLabel, fmt]);

  return null;
}

export function Overlays() {
  const classes = useOverlayClasses();
  return (
    <>
      {classes.map((cls) => (
        <OverlayLayer key={cls.id} cls={cls} />
      ))}
    </>
  );
}

export default Overlays;
